import { getPaymentConfig } from "../config/paymentConfig";
import { generateTransactionNumber } from "../utils/paymentUtils";
import axiosInstance from "./AxiosInstance";

class PaymentLinkService {
  constructor() {
    this.config = getPaymentConfig();
    this.paymentLinks = this.loadPaymentLinks();
  }

  // Load payment links from localStorage
  loadPaymentLinks() {
    try {
      const links = localStorage.getItem("paymentLinks");
      return links ? JSON.parse(links) : [];
    } catch (error) {
      console.error("Error loading payment links:", error);
      return [];
    }
  }

  // Save payment links to localStorage
  savePaymentLinks() {
    try {
      localStorage.setItem("paymentLinks", JSON.stringify(this.paymentLinks));
    } catch (error) {
      console.error("Error saving payment links:", error);
    }
  }

  // Generate a unique payment link ID
  generateLinkId() {
    return `PL_${Date.now()}_${Math.random().toString(36).substr(2, 9)}`;
  }

  // Send payment link via backend API
  async sendPaymentLink(paymentData) {
    try {
      const requestPayload = {
        token: localStorage.getItem("token"),
        admin_id: localStorage.getItem("admin_id"),
        patient_id: paymentData.patient_id,
        payment_link: paymentData.payment_link,
        lead_id: paymentData.lead_id || null,
        appointment_id: paymentData.appointment_id || null,
        payment_for: paymentData.payment_for || "Patient Queue",
        prescription_id: paymentData.prescription_id || null,
        queue_id: paymentData.queue_id || null,
      };

      // Filter out zero values from request payload
      const filteredPayload = {};
      Object.keys(requestPayload).forEach((key) => {
        const value = requestPayload[key];
        // Skip if value is "0", 0, null, undefined, or empty string
        if (
          value !== "0" &&
          value !== 0 &&
          value !== null &&
          value !== undefined &&
          value !== ""
        ) {
          filteredPayload[key] = value;
        }
      });

      const response = await axiosInstance.post(
        "/payment/sendPaymentLink",
        filteredPayload
      );

      if (response.data?.status === "success") {
        return {
          success: true,
          data: response.data.data,
          message: response.data.message || "Payment link sent successfully",
        };
      } else {
        throw new Error(
          response.data?.message || "Failed to send payment link"
        );
      }
    } catch (error) {
      console.error("Error sending payment link:", error);
      return {
        success: false,
        error:
          error.response?.data?.message ||
          error.message ||
          "Failed to send payment link",
      };
    }
  }

  // Get payment link data from backend API
  async getPaymentLinkData( patientId, additionalData = {}) {
    try {
      const requestPayload = {

        patient_id: patientId,
        // Include additional data if provided in snake_case format
        appointment_id: additionalData?.appointmentId || null,
        lead_id: additionalData?.leadId || null,
        prescription_id: additionalData?.prescriptionId || null,
        queue_id: additionalData?.queueId || null,
        payment_for: additionalData?.paymentFor || "Patient Queue",
      };

      // Filter out zero values from request payload
      const filteredPayload = {};
      Object.keys(requestPayload).forEach((key) => {
        const value = requestPayload[key];
        // Skip if value is "0", 0, null, undefined, or empty string
        if (
          value !== "0" &&
          value !== 0 &&
          value !== null &&
          value !== undefined &&
          value !== ""
        ) {
          filteredPayload[key] = value;
        }
      });

      const response = await axiosInstance.post(
        "/payment/getPaymentLinkData",
        filteredPayload
      );

      if (response.data?.status === "success") {
        return {
          success: true,
          data: response.data.data,
          message:
            response.data.message || "Payment link data retrieved successfully",
        };
      } else {
        throw new Error(
          response.data?.message || "Failed to get payment link data"
        );
      }
    } catch (error) {
      console.error("Error getting payment link data:", error);
      return {
        success: false,
        error:
          error.response?.data?.message ||
          error.message ||
          "Failed to get payment link data",
      };
    }
  }

  // Update payment status via backend API
  async updatePaymentStatus(paymentData) {
    try {
      const requestPayload = {
        token: localStorage.getItem("token"),
        admin_id: localStorage.getItem("admin_id"),
        patient_id: paymentData.patient_id || paymentData.patientId,
        status: paymentData.status,
        transaction_id: paymentData.transaction_id || paymentData.transactionId,
        payment_details:
          paymentData.payment_details || paymentData.paymentDetails || {},
      };

      // Filter out zero values from request payload
      const filteredPayload = {};
      Object.keys(requestPayload).forEach((key) => {
        const value = requestPayload[key];
        // Skip if value is "0", 0, null, undefined, or empty string
        if (
          value !== "0" &&
          value !== 0 &&
          value !== null &&
          value !== undefined &&
          value !== ""
        ) {
          filteredPayload[key] = value;
        }
      });

      const response = await axiosInstance.post(
        "/payment/updatePaymentStatus",
        filteredPayload
      );

      if (response.data?.status === "success") {
        return {
          success: true,
          data: response.data.data,
          message:
            response.data.message || "Payment status updated successfully",
        };
      } else {
        throw new Error(
          response.data?.message || "Failed to update payment status"
        );
      }
    } catch (error) {
      console.error("Error updating payment status:", error);
      return {
        success: false,
        error:
          error.response?.data?.message ||
          error.message ||
          "Failed to update payment status",
      };
    }
  }

  // Update payment status via backend API with specific payload format
  async updatePaymentStatusToBackend(paymentData) {
    try {
      const requestPayload = {
        token: localStorage.getItem("token"),
        admin_id: localStorage.getItem("admin_id"),
        patient_id: paymentData.patient_id || paymentData.patientId,
        payment_status:
          paymentData.payment_status || paymentData.status || "success",
        payment_response:
          paymentData.payment_response || JSON.stringify(paymentData),
        transaction_id:
          paymentData.transaction_id || paymentData.transactionId || "",
        amount: paymentData.amount || 0,
        payment_type:
          paymentData.payment_type || paymentData.paymentType || "UPI",
        payment_for:
          paymentData.payment_for || paymentData.paymentFor || "APPOINTMENT",
        queue_id: paymentData.queue_id || paymentData.queueId || "",
        coupon_code: paymentData.coupon_code || paymentData.couponCode || "",
        final_amount:
          paymentData.final_amount ||
          paymentData.finalAmount ||
          paymentData.amount ||
          0,
        reference_id: paymentData.reference_id || paymentData.referenceId || "",
      };

      // Filter out empty values but keep 0 values for amounts
      const filteredPayload = {};
      Object.keys(requestPayload).forEach((key) => {
        const value = requestPayload[key];
        // Keep 0 values for amounts, but skip null, undefined, or empty string for other fields
        if (
          key === "amount" || key === "final_amount"
            ? value !== null && value !== undefined
            : value !== null && value !== undefined && value !== ""
        ) {
          filteredPayload[key] = value;
        }
      });

      console.log(
        "📤 Sending payment status update to backend:",
        filteredPayload
      );

      const response = await axiosInstance.post(
        "/payment/updatePaymentStatus",
        filteredPayload
      );

      if (response.data?.status === "success") {
        console.log("✅ Payment status updated successfully:", response.data);
        return {
          success: true,
          data: response.data.data,
          message:
            response.data.message || "Payment status updated successfully",
        };
      } else {
        throw new Error(
          response.data?.message || "Failed to update payment status"
        );
      }
    } catch (error) {
      console.error("❌ Error updating payment status:", error);
      return {
        success: false,
        error:
          error.response?.data?.message ||
          error.message ||
          "Failed to update payment status",
      };
    }
  }

  // Create a new payment link (local storage for now, can be enhanced later)
  createPaymentLink(paymentData) {
    try {
      const linkId = this.generateLinkId();
      const transactionNumber = generateTransactionNumber();

      const paymentLink = {
        id: linkId,
        transactionNumber,
        status: "PENDING", // PENDING, PAID, EXPIRED, CANCELLED
        createdAt: new Date().toISOString(),
        expiresAt: new Date(Date.now() + 24 * 60 * 60 * 1000).toISOString(), // 24 hours
        paymentData: {
          ...paymentData,
          transactionNumber,
          linkId,
        },
        metadata: {
          createdBy: paymentData.createdBy || "Telecaller",
          patientName: paymentData.customerName,
          patientPhone: paymentData.customerPhone,
          patientEmail: paymentData.customerEmail,
          amount: paymentData.amount,
          description: paymentData.description,
          paymentType: paymentData.paymentType || "appointment",
          notes: paymentData.notes || "",
        },
        paymentUrl: `${window.location.origin}/payment/link/${linkId}`,
        shortUrl: this.generateShortUrl(linkId),
      };

      // Add to local storage
      this.paymentLinks.unshift(paymentLink);
      this.savePaymentLinks();

      return {
        success: true,
        data: paymentLink,
      };
    } catch (error) {
      console.error("Error creating payment link:", error);
      return {
        success: false,
        error: error.message || "Failed to create payment link",
      };
    }
  }

  // Generate a short URL for easier sharing
  generateShortUrl(linkId) {
    // For now, we'll use a simple short URL
    // In production, you might want to use a URL shortener service
    return `${window.location.origin}/pay/${linkId}`;
  }

  // Get payment link by ID
  getPaymentLink(linkId) {
    return this.paymentLinks.find((link) => link.id === linkId);
  }

  // Get all payment links
  getAllPaymentLinks() {
    return this.paymentLinks;
  }

  // Get payment links by status
  getPaymentLinksByStatus(status) {
    return this.paymentLinks.filter((link) => link.status === status);
  }

  // Update payment link status
  updatePaymentLinkStatus(linkId, status, paymentDetails = null) {
    const linkIndex = this.paymentLinks.findIndex((link) => link.id === linkId);

    if (linkIndex !== -1) {
      this.paymentLinks[linkIndex].status = status;
      this.paymentLinks[linkIndex].updatedAt = new Date().toISOString();

      if (paymentDetails) {
        this.paymentLinks[linkIndex].paymentDetails = paymentDetails;
      }

      this.savePaymentLinks();
      return true;
    }

    return false;
  }

  // Check if payment link is expired
  isLinkExpired(linkId) {
    const link = this.getPaymentLink(linkId);
    if (!link) return true;

    return new Date() > new Date(link.expiresAt);
  }

  // Delete payment link
  deletePaymentLink(linkId) {
    const linkIndex = this.paymentLinks.findIndex((link) => link.id === linkId);

    if (linkIndex !== -1) {
      this.paymentLinks.splice(linkIndex, 1);
      this.savePaymentLinks();
      return true;
    }

    return false;
  }

  // Resend payment link (create new one with same data)
  resendPaymentLink(originalLinkId) {
    const originalLink = this.getPaymentLink(originalLinkId);
    if (!originalLink) {
      return {
        success: false,
        error: "Original payment link not found",
      };
    }

    // Create new link with same payment data
    const newPaymentData = {
      ...originalLink.paymentData,
      createdBy: originalLink.metadata.createdBy,
      notes: `Resent from original link: ${originalLinkId}`,
    };

    return this.createPaymentLink(newPaymentData);
  }

  // Get payment link statistics
  getPaymentLinkStats() {
    const total = this.paymentLinks.length;
    const pending = this.paymentLinks.filter(
      (link) => link.status === "PENDING"
    ).length;
    const paid = this.paymentLinks.filter(
      (link) => link.status === "PAID"
    ).length;
    const expired = this.paymentLinks.filter(
      (link) => link.status === "EXPIRED"
    ).length;
    const cancelled = this.paymentLinks.filter(
      (link) => link.status === "CANCELLED"
    ).length;

    const totalAmount = this.paymentLinks
      .filter((link) => link.status === "PAID")
      .reduce((sum, link) => sum + parseFloat(link.metadata.amount), 0);

    return {
      total,
      pending,
      paid,
      expired,
      cancelled,
      totalAmount: totalAmount.toFixed(2),
      successRate: total > 0 ? ((paid / total) * 100).toFixed(1) : 0,
    };
  }

  // Search payment links
  searchPaymentLinks(query) {
    const searchTerm = query.toLowerCase();
    return this.paymentLinks.filter(
      (link) =>
        link.metadata.patientName.toLowerCase().includes(searchTerm) ||
        link.metadata.patientPhone.includes(searchTerm) ||
        link.metadata.patientEmail.toLowerCase().includes(searchTerm) ||
        link.id.toLowerCase().includes(searchTerm) ||
        link.transactionNumber.toLowerCase().includes(searchTerm)
    );
  }

  // Export payment links data
  exportPaymentLinks(format = "json") {
    if (format === "csv") {
      return this.exportToCSV();
    }
    return JSON.stringify(this.paymentLinks, null, 2);
  }

  // Export to CSV format
  exportToCSV() {
    const headers = [
      "Link ID",
      "Transaction Number",
      "Status",
      "Patient Name",
      "Patient Phone",
      "Patient Email",
      "Amount",
      "Description",
      "Created At",
      "Expires At",
      "Created By",
    ];

    const csvData = this.paymentLinks.map((link) => [
      link.id,
      link.transactionNumber,
      link.status,
      link.metadata.patientName,
      link.metadata.patientPhone,
      link.metadata.patientEmail,
      link.metadata.amount,
      link.metadata.description,
      link.createdAt,
      link.expiresAt,
      link.metadata.createdBy,
    ]);

    const csvContent = [headers, ...csvData]
      .map((row) => row.map((field) => `"${field}"`).join(","))
      .join("\n");

    return csvContent;
  }
}

// Create singleton instance
const paymentLinkService = new PaymentLinkService();
export default paymentLinkService;
